import Switch, {SwitchProps} from '@mui/material/Switch';
import {styled} from '@mui/material/styles';
import {useRef} from 'react';
import { useTranslation } from 'react-i18next';

type AurSwitchProps = SwitchProps & {
  ariaLabel?: string;
  dataTestId?: string;
  disabled?:boolean;
};

const SwitchWrapper = styled('div')(() => ({
  position: 'relative',
  display: 'inline-block',
  '&:focus-within': {
    outline: 'none',
    outlineOffset: 'none',
  },
}));

const Android12Switch = styled(Switch)(() => ({
  marginLeft: -8,
  padding: 8,
  width: '60px',
  '& .MuiSwitch-track': {
    borderRadius: 22 / 2,
    backgroundColor: '#282c34',
    border: '1px solid #8596B0',
    opacity: 1,
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      transform: 'translateY(-50%)',
      width: 8,
      height: 8,
      top: '50%',
    },
    '&::before': {
      backgroundImage: `url('/assests/images/knob.svg')`,
      left: 14,
    },
    '&::after': {
      backgroundImage: `url('/assests/images/off.svg')`,
      right: 14,
    },
  },
  '.MuiButtonBase-root.Mui-checked+.MuiSwitch-track': {
    backgroundColor: '#5265FF',
    opacity: 1,
    borderColor: '#5265FF',
  },
  '.MuiButtonBase-root.Mui-checked.Mui-disabled+.MuiSwitch-track': {
    backgroundColor: '#424750',
    opacity: 1,
    borderColor: '#424750',
    cursor: 'not-allowed',
  },
  '.Mui-checked .MuiSwitch-thumb': {
    backgroundColor: '#0C1119',
    opacity: 1,
    borderColor: '#0C1119',
    margin: '2px 4px',
  },
  '& .MuiSwitch-thumb': {
    boxShadow: 'none',
    width: 16,
    height: 16,
    margin: 2,
    backgroundColor: '#282c34',
    border: '1px solid #8596B0',
  },
  '& .MuiSwitch-input:focus + .MuiSwitch-track': {
    borderColor: 'white',
    borderWidth: '2px',
    borderStyle: 'solid',
  },
}));
export const AurSwitch = (props: AurSwitchProps) => {
  const {t} = useTranslation();
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && onChange) {
      event.preventDefault(); // Prevent form submission (if inside a form)
      if (onChange) {
        onChange({target: {value: !props.value}} as any, !props.value);
      }
    }
  };
  const {disabled = false, ariaLabel, dataTestId = 'toggle-switch', onChange, ...rest} = props;

  const switchRef = useRef<HTMLDivElement>(null);

  const updateTrackStyle = (isFocused: boolean) => {
    if (switchRef.current) {
      const track = switchRef.current.querySelector(
        '.MuiSwitch-track'
      ) as HTMLElement;
      if (track) {
        if (isFocused) {
          track.style.outline = '2px solid white'; // ✅ Add outline
          track.style.outlineOffset = '1px';
        } 
        else {
          track.style.borderColor = '#8596B0'; // Default
          track.style.boxShadow = 'none';
          track.style.outline = 'none'; // Reset outline
        }
      }
    }
  };

  const onFocus = () => {
    updateTrackStyle(true);
  };
  const onBlur = () => {
    updateTrackStyle(false);
  };

  return (
    <SwitchWrapper
      onFocus={onFocus}
      onBlur={onBlur}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      ref={switchRef}
    >
      <Android12Switch
        disabled={disabled}
        disableRipple
        data-testid={dataTestId}
        inputProps={{"aria-label": ariaLabel || t('aria.toggle'), role: 'switch'}}
        checked={!!props.value}
        onChange={onChange}
        {...rest}
      />
    </SwitchWrapper>
  );
};
